import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import type { 
    AssignableUser,
    UpdateClaimAccessRequestBlockedModulesEnum,
} from '@/api/generated/client/models';
import { ShareClaimRequestOneOf2TypeEnum } from '@/api/generated/client/models';

export function useClaim(claimId: number, options?: { enabled?: boolean }) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: claim, isLoading, error } = useQuery({
        queryKey: ['claims', claimId],
        queryFn: async () => {
            if (!claimId) return null;
            return await apiClient.claims.getClaim({ claimId });
        },
        enabled: !!claimId && (options?.enabled ?? true),
    });

    const updateClaimMutation = useMutation({
        mutationFn: async (updatedData: any) => {
            return await apiClient.claims.updateClaim({
                claimId,
                updateClaimRequest: updatedData
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['claims', claimId] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            toast({
                title: "Claim updated",
                description: "The claim details have been successfully updated.",
            });
        },
        onError: (error) => {
            toast({
                title: "Failed to update claim",
                description: "There was an error saving your changes.",
                variant: "destructive",
            });
            console.error("Update claim error:", error);
        }
    });

    return {
        claim,
        isLoading,
        error,
        updateClaim: updateClaimMutation.mutateAsync,
        isUpdating: updateClaimMutation.isPending
    };
}

/**
 * Hook for managing claim assignees (user assignments via claim_access)
 * 
 * Assignee data with shareIds is embedded in the claim detail response,
 * so we only need to fetch assignable users separately.
 */
export function useClaimAssignees(
    claimId: number,
    options: { assignableEnabled?: boolean } = {},
) {
    const { assignableEnabled = true } = options;
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { firebaseUser } = useAuth();
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
    const [isAssigning, setIsAssigning] = useState(false);

    // Fetch assignable users (company members not yet assigned to this claim)
    // Gate on auth readiness to prevent token provider errors during initial load.
    // `assignableEnabled` lets callers (e.g. list rows) defer the request until needed.
    const { data: assignableData, isLoading: isLoadingAssignable } = useQuery({
        queryKey: ["claims", claimId, "assignable-users"],
        queryFn: () => apiClient.claims.getAssignableUsers({ claimId }),
        enabled: !!claimId && !!firebaseUser && assignableEnabled,
    });

    const assignableUsers: AssignableUser[] = assignableData?.data ?? [];

    // Assign user mutation
    const assignMutation = useMutation({
        mutationFn: async ({
            userId,
            level,
            blockedModules,
        }: {
            userId: number;
            level: "ro" | "rw";
            blockedModules?: UpdateClaimAccessRequestBlockedModulesEnum[];
        }) => {
            return apiClient.claims.shareClaim({
                claimId,
                shareClaimRequest: {
                    type: "user",
                    userId,
                    level,
                                        blockedModules,
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            queryClient.invalidateQueries({ queryKey: ["claims", claimId] });
            queryClient.invalidateQueries({ queryKey: ["claims", claimId, "assignable-users"] });
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            toast({ title: "User assigned", description: "The user has been assigned to this claim." });
        },
        onError: () => {
            toast({ title: "Failed to assign user", description: "There was an error assigning the user.", variant: "destructive" });
        },
    });

    // Revoke assignment mutation
    const revokeMutation = useMutation({
        mutationFn: async (shareId: number) => {
            return apiClient.claims.revokeClaimAccess({
                claimId,
                shareId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            queryClient.invalidateQueries({ queryKey: ["claims", claimId] });
            queryClient.invalidateQueries({ queryKey: ["claims", claimId, "assignable-users"] });
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            toast({ title: "User unassigned", description: "The user has been removed from this claim." });
        },
        onError: () => {
            toast({ title: "Failed to unassign user", description: "There was an error removing the user.", variant: "destructive" });
        },
    });

    // Revoke a pending email invite (person who has not signed up yet)
    const revokeInviteMutation = useMutation({
        mutationFn: async (inviteId: number) => {
            return apiClient.claims.revokeClaimInvite({
                claimId,
                inviteId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            queryClient.invalidateQueries({ queryKey: ["claims", claimId] });
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            toast({ title: "Invite revoked", description: "The pending invite has been removed." });
        },
        onError: () => {
            toast({ title: "Failed to revoke invite", description: "There was an error removing the invite.", variant: "destructive" });
        },
    });

    const assignUser = async (
        userId: string,
        level: "ro" | "rw" = "rw",
        blockedModules?: UpdateClaimAccessRequestBlockedModulesEnum[],
    ) => {
        setIsAssigning(true);
        setSelectedUserId(userId);
        try {
            await assignMutation.mutateAsync({ userId: parseInt(userId), level, blockedModules });
            // Clear selection after successful assignment
            setSelectedUserId(undefined);
        } catch {
            // Clear selection on error
            setSelectedUserId(undefined);
        } finally {
            setIsAssigning(false);
        }
    };

    const revokeUser = (shareId: number) => {
        if (shareId) {
            revokeMutation.mutate(shareId);
        }
    };

    // Invite external user by email (cross-company sharing)
    const inviteByEmailMutation = useMutation({
        mutationFn: async ({
            email,
            level,
            blockedModules,
        }: {
            email: string;
            level: "ro" | "rw";
            blockedModules?: UpdateClaimAccessRequestBlockedModulesEnum[];
        }) => {
            return apiClient.claims.shareClaim({
                claimId,
                shareClaimRequest: {
                    type: ShareClaimRequestOneOf2TypeEnum.Email,
                    email,
                    level,
                    blockedModules: blockedModules as unknown as object[] | undefined,
                },
            });
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["claims", claimId] });
            queryClient.invalidateQueries({ queryKey: ["claims", claimId, "assignable-users"] });
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            if (response?.pending) {
                toast({
                    title: "Invitation sent",
                    description: "We've emailed them an invite to create an account and access this claim.",
                });
            } else {
                toast({ title: "User invited", description: "The user has been granted access to this claim." });
            }
        },
        onError: (error: unknown) => {
            let message = "There was an error inviting this email address.";
            if (error && typeof error === 'object' && 'body' in error) {
                const body = (error as Record<string, unknown>).body;
                if (body && typeof body === 'object' && 'detail' in body) {
                    const detail = (body as Record<string, unknown>).detail;
                    if (typeof detail === 'string') message = detail;
                }
            } else if (error instanceof Error) {
                message = error.message;
            }
            toast({ title: "Failed to invite user", description: message, variant: "destructive" });
        },
    });

    const inviteByEmail = async (
        email: string,
        level: "ro" | "rw" = "rw",
        blockedModules?: UpdateClaimAccessRequestBlockedModulesEnum[],
    ) => {
        await inviteByEmailMutation.mutateAsync({ email, level, blockedModules });
    };

    const revokeInvite = (inviteId: number) => {
        if (inviteId) {
            revokeInviteMutation.mutate(inviteId);
        }
    };

    const updateAccessMutation = useMutation({
        mutationFn: async ({
            shareId,
            accessLevel,
            blockedModules,
        }: {
            shareId: number;
            accessLevel?: 'ro' | 'rw';
            blockedModules?: UpdateClaimAccessRequestBlockedModulesEnum[];
        }) => {
            return apiClient.claims.updateClaimAccess({
                claimId,
                shareId,
                updateClaimAccessRequest: {
                    accessLevel,
                    blockedModules,
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['claims'] });
            queryClient.invalidateQueries({ queryKey: ['claims', claimId] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            toast({ title: 'Permissions updated', description: 'Claim access permissions were updated.' });
        },
        onError: () => {
            toast({ title: 'Failed to update permissions', description: 'There was an error saving permissions.', variant: 'destructive' });
        },
    });

    const updateUserAccess = async ({
        shareId,
        accessLevel,
        blockedModules,
    }: {
        shareId: number;
        accessLevel?: 'ro' | 'rw';
        blockedModules?: UpdateClaimAccessRequestBlockedModulesEnum[];
    }) => {
        await updateAccessMutation.mutateAsync({ shareId, accessLevel, blockedModules });
    };

    return {
        assignableUsers,
        isLoadingAssignable,
        selectedUserId,
        isAssigning,
        isRevoking: revokeMutation.isPending,
        isInvitingByEmail: inviteByEmailMutation.isPending,
        isRevokingInvite: revokeInviteMutation.isPending,
        assignUser,
        revokeUser,
        inviteByEmail,
        revokeInvite,
        updateUserAccess,
        isUpdatingAccess: updateAccessMutation.isPending,
    };
}

